import {
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
  IconButton,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import React, { useState } from "react";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { login_admin } from "../../Services/api";
import logo from "../../logo512.png";

const Login = () => {
  const [txtEmail, setTxtEmail] = useState("");
  const [txtPassword, setTxtPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Color definitions
  const primaryColor = "#F4B400";
  const secondaryColor = "#0F9D58"; // Complementary green
  const accentColor = "#DB4437"; // Complementary red
  const darkColor = "#333333";
  const lightColor = "#F5F5F5";

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleChange = (e) => {
    if (e.target.name === "txtEmail") {
      setTxtEmail(e.target.value);
    } else if (e.target.name === "txtPassword") {
      setTxtPassword(e.target.value);
    }
  };

  const isValidEmail = (val) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(val);
  };

  const handleClick = async () => {
    const loginObj = {
      email: txtEmail,
      password: txtPassword,
    };
    if (!txtEmail.trim()) {
      alert("Please enter email address.");
      return;
    }
    if (!isValidEmail(txtEmail)) {
      alert("Please enter valid email address.");
      return;
    }
    if (!txtPassword.trim()) {
      alert("Please enter password.");
      return;
    }
    setLoading(true);
    try {
      const result = await login_admin(loginObj);
      localStorage.setItem("loginData", JSON.stringify(result.data));
      localStorage.setItem("isLoggedIn", "true");
      navigate("/dashboard");
    } catch (error) {
      console.error(error);
      alert("Login failed. Please check your credentials and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleClick();
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        p: isMobile ? 2 : 0,
        background: `linear-gradient(135deg, ${lightColor} 0%, #ffffff 100%)`,
      }}
    >
      {/* Animated Background Elements */}
      {/* Floating Elements with Gold Theme */}
      {[...Array(8)].map((_, i) => (
        <Box
          key={`decor-${i}`}
          sx={{
            position: "absolute",
            color: i % 2 === 0 ? primaryColor : secondaryColor,
            fontSize: `${Math.random() * 20 + 10}px`,
            opacity: 0.6,
            animation: `float ${Math.random() * 10 + 10}s linear infinite`,
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
            transform: `scale(${Math.random() * 0.5 + 0.5})`,
            "@keyframes float": {
              "0%": {
                transform: "translateY(0) rotate(0deg)",
              },
              "50%": {
                transform: "translateY(-100px) rotate(180deg)",
              },
              "100%": {
                transform: "translateY(0) rotate(360deg)",
              },
            },
          }}
        >
          {i % 3 === 0 ? "✦" : i % 3 === 1 ? "✧" : "❖"}
        </Box>
      ))}

      <Grid container justifyContent="center">
        <Grid item xs={12} sm={8} md={6} lg={4}>
          <Card
            sx={{
              p: isMobile ? 2 : 4,
              borderRadius: 4,
              boxShadow: `0 8px 32px rgba(244, 180, 0, 0.2)`,
              border: `1px solid rgba(244, 180, 0, 0.2)`,
              backdropFilter: "blur(8px)",
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              position: "relative",
              zIndex: 1,
              "&:hover": {
                boxShadow: `0 8px 32px rgba(244, 180, 0, 0.3)`,
              },
              transition: "all 0.3s ease",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mb: 4,
              }}
            >
              <img
                src={logo}
                alt="Apna Kayasth Vivah Logo"
                style={{
                  width: isMobile ? "180px" : "220px",
                  height: "auto",
                  marginBottom: "16px",
                  objectFit: "contain",
                  filter: `drop-shadow(0 2px 4px ${primaryColor}40)`,
                }}
              />
              <Typography
                variant="h5"
                component="h1"
                sx={{
                  fontWeight: 700,
                  color: darkColor,
                  textAlign: "center",
                  mb: 1,
                  textShadow: `0 2px 4px ${primaryColor}20`,
                }}
              >
                Apna Kayasth Vivah Portal
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: darkColor,
                  textAlign: "center",
                  maxWidth: "80%",
                  opacity: 0.8,
                }}
              >
                Sign in to manage your matrimony platform
              </Typography>
            </Box>

            <CardContent sx={{ px: isMobile ? 0 : 2 }}>
              <TextField
                id="txtEmail"
                label="Email Address"
                variant="outlined"
                name="txtEmail"
                value={txtEmail}
                fullWidth
                onChange={handleChange}
                onKeyPress={handleKeyPress}
                sx={{ mb: 3 }}
                InputProps={{
                  sx: {
                    borderRadius: 2,
                    "&:focus-within fieldset": {
                      borderColor: `${primaryColor} !important`,
                    },
                  },
                }}
                InputLabelProps={{
                  sx: {
                    "&.Mui-focused": {
                      color: primaryColor,
                    },
                  },
                }}
              />
              <TextField
                id="txtPassword"
                label="Password"
                variant="outlined"
                name="txtPassword"
                value={txtPassword}
                fullWidth
                type={showPassword ? "text" : "password"}
                onChange={handleChange}
                onKeyPress={handleKeyPress}
                sx={{ mb: 2 }}
                InputProps={{
                  sx: {
                    borderRadius: 2,
                    "&:focus-within fieldset": {
                      borderColor: `${primaryColor} !important`,
                    },
                  },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={togglePasswordVisibility}
                        edge="end"
                        aria-label="toggle password visibility"
                        sx={{ color: primaryColor }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{
                  sx: {
                    "&.Mui-focused": {
                      color: primaryColor,
                    },
                  },
                }}
              />
              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={handleClick}
                disabled={loading}
                sx={{
                  mt: 2,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: "1rem",
                  fontWeight: 600,
                  backgroundColor: primaryColor,
                  color: darkColor,
                  "&:hover": {
                    backgroundColor: "#E6A800",
                    boxShadow: `0 4px 8px ${primaryColor}80`,
                  },
                  "&.Mui-disabled": {
                    backgroundColor: `${primaryColor}80`,
                    color: `${darkColor}80`,
                  },
                  boxShadow: `0 3px 5px ${primaryColor}40`,
                  transition: "all 0.3s ease",
                }}
                startIcon={
                  loading && <CircularProgress size={20} color="inherit" />
                }
              >
                {loading ? "Signing in..." : "Sign In"}
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Login;
